import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { RoiChange } from '@analysis/domain';

// TODO: convert to entity state
export interface RoiChangeState {
  changedRois: Map<string, RoiChange>;
  changeCountPerLabel: Map<string, number>; // label -> count of changes
  changesRestored: boolean;
  changeRestorationComplete: boolean;
  syncing: boolean;
  applyingChanges: boolean;
  revertingChanges: boolean;
  error: string | undefined;
}

@Injectable()
export class RoiChangeStore extends ComponentStore<RoiChangeState> {
  constructor() {
    super({
      changedRois: new Map(),
      changeCountPerLabel: new Map(),
      changesRestored: false,
      changeRestorationComplete: false,
      syncing: false,
      applyingChanges: false,
      revertingChanges: false,
      error: undefined,
    });
  }

  public readonly changedRois$ = this.select((state) => state.changedRois);
  public readonly changeCountPerLabel$ = this.select((state) => state.changeCountPerLabel);
  public readonly changesRestored$ = this.select((state) => state.changesRestored);
  public readonly changeRestorationComplete$ = this.select((state) => state.changeRestorationComplete);
  public readonly unsynced$ = this.select((state) => state.changedRois.size > 0);
  public readonly synced$ = this.select(this.unsynced$, (unsynced) => !unsynced);
  public readonly syncing$ = this.select((state) => state.syncing);
  public readonly applyingChanges$ = this.select((state) => state.applyingChanges);
  public readonly revertingChanges$ = this.select((state) => state.revertingChanges);
  public readonly error$ = this.select((state) => state.error);

  public readonly markChangeRestorationComplete = this.updater((state) => ({
    ...state,
    changeRestorationComplete: true,
  }));

  public readonly setSyncing = this.updater((state, syncing: boolean) => ({
    ...state,
    syncing,
    ...(syncing ? { error: undefined } : {}),
  }));

  public readonly setApplyingChanges = this.updater((state, applyingChanges: boolean) => ({
    ...state,
    applyingChanges,
  }));

  public readonly setRevertingChanges = this.updater((state, revertingChanges: boolean) => ({
    ...state,
    revertingChanges,
  }));

  public readonly setError = this.updater((state, error: string) => ({
    ...state,
    error: error,
    syncing: false,
  }));

  public readonly addChanges = this.updater((state: RoiChangeState, changes: Map<string, RoiChange | null>) => {
    const newChangedRois = new Map(state.changedRois);
    for (const [id, change] of changes) {
      if (change !== null) {
        newChangedRois.set(id, change);
      } else {
        newChangedRois.delete(id);
      }
    }
    return {
      ...state,
      changedRois: newChangedRois,
      changeCountPerLabel: RoiChangeStore.computeChangeCountPerLabel(newChangedRois),
    };
  });

  public readonly setRestoredChanges = this.updater((state, changes: Map<string, RoiChange>) => ({
    ...state,
    changedRois: changes,
    changeCountPerLabel: RoiChangeStore.computeChangeCountPerLabel(changes),
    changesRestored: changes.size > 0,
    changeRestorationComplete: true,
  }));

  public readonly clearAllChanges = this.updater((state) => ({
    ...state,
    changedRois: new Map(),
    changeCountPerLabel: new Map(),
    syncing: false,
  }));

  public readonly resetForRestoration = this.updater((state) => ({
    ...state,
    changeRestorationComplete: false,
    changesRestored: false,
    changeCountPerLabel: new Map(),
    changedRois: new Map(),
  }));

  private static computeChangeCountPerLabel(changedRois: Map<string, RoiChange>): Map<string, number> {
    const counts = new Map<string, number>();
    for (const change of changedRois.values()) {
      for (const label of change.labelsAdded) {
        counts.set(label, (counts.get(label) ?? 0) + 1);
      }
      for (const label of change.labelsRemoved) {
        counts.set(label, (counts.get(label) ?? 0) - 1);
      }
    }
    return counts;
  }
}
